import { VALIDATION, STORAGE_KEYS } from '../constants';

// Form validation utilities
export const validateEmail = (email: string): boolean => {
  return VALIDATION.EMAIL_REGEX.test(email);
};

export const validateMessage = (message: string): { isValid: boolean; error?: string } => {
  if (message.length < VALIDATION.MIN_MESSAGE_LENGTH) {
    return { isValid: false, error: `Mensagem deve ter pelo menos ${VALIDATION.MIN_MESSAGE_LENGTH} caracteres` };
  }
  if (message.length > VALIDATION.MAX_MESSAGE_LENGTH) {
    return { isValid: false, error: `Mensagem deve ter no máximo ${VALIDATION.MAX_MESSAGE_LENGTH} caracteres` };
  }
  return { isValid: true };
};

// Local storage utilities
export const storage = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },
};

// Session utilities
export const sessionUtils = {
  generateSessionId: (): string => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  getUserSession: () => {
    return storage.get(STORAGE_KEYS.USER_SESSION);
  },

  setUserSession: (session: any) => {
    storage.set(STORAGE_KEYS.USER_SESSION, session);
  },

  clearUserSession: () => {
    storage.remove(STORAGE_KEYS.USER_SESSION);
  },
};

// Animation utilities
export const animationUtils = {
  staggerDelay: (index: number, baseDelay: number = 200): number => {
    return baseDelay * index;
  },

  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
};

// Format utilities
export const formatUtils = {
  formatNumber: (num: number): string => {
    return new Intl.NumberFormat('pt-BR').format(num);
  },

  formatDate: (date: Date): string => {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  },

  formatTime: (date: Date): string => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  },

  truncateText: (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  },
};

// Device detection utilities
export const deviceUtils = {
  isMobile: (): boolean => {
    return window.innerWidth <= 768;
  },

  isTablet: (): boolean => {
    return window.innerWidth > 768 && window.innerWidth <= 1024;
  },

  isDesktop: (): boolean => {
    return window.innerWidth > 1024;
  },

  isTouchDevice: (): boolean => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },
};

// Voice recognition utilities
export const voiceUtils = {
  isSupported: (): boolean => {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  },

  createRecognition: () => {
    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.lang = 'pt-BR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    return recognition;
  },
};

// URL utilities
export const urlUtils = {
  createWhatsAppUrl: (message: string, phone?: string): string => {
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = phone || '5511999999999';
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  },

  openInNewTab: (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  },
};

// Progress calculation utilities
export const progressUtils = {
  calculateInputProgress: (messageLength: number): number => {
    let progress = 0;
    if (messageLength > 0) progress += 30;
    if (messageLength > 10) progress += 20;
    if (messageLength > 50) progress += 30;
    return Math.min(progress, 80); // Max 80% for input, 100% for completion
  },
};

// Debounce utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle utility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}; 